// ── HonestState: the one router for the 9 honest postures (reused everywhere) ──
// Wave 0 foundation, blueprint §4. Every coaching list / section / detail body
// wraps its content in <HonestState>; pages never hand-roll state branching.
//
// Release gate (asserted in tests): no surface may render a gate / gate-closed /
// not-configured / unavailable / permission-denied response as a plain empty
// list, and `loading` is a skeleton — never a blank. EVERY branch stamps
// `data-honest-posture="<posture>"` so the live e2e lane can wait for and assert
// the rendered posture.
//
// RENDERING is delegated to @vjal/ui <AsyncState> for the two surfaces it covers
// identically — the full-page loading block (PageLoading) and the error/outage
// Alert + Retry. The coaching-specific surfaces stay LOCAL: the gate-closed
// warning ("No change was made"), the empty-family EmptyState (navy-tint domain
// glyph + create-CTA-only-on-empty), and the rows-skeleton sizing knobs. The
// 9-posture vocabulary + neutral copy live in honest-posture.ts; the CO_* domain
// error→posture MAPPER stays in services/coaching-error-posture (via useHonestQuery).
// Each branch wraps its content in a single `data-honest-posture` node so the
// e2e/spec selectors are unchanged even though @vjal/ui stamps its own
// (orthogonal) `data-async-state` inside.

import type { ReactNode } from "react";
import { Alert, AsyncState, EmptyState, IconSearch, SkeletonRows } from "@vjal/ui";
import type { HonestPosture } from "./honest-posture";
import { POSTURE_COPY, isRetryablePosture } from "./honest-posture";
import type { HonestGate } from "../../services/coaching-error-posture";

export interface HonestStateProps {
  posture: HonestPosture;
  /** Closed-gate descriptor (gate-closed copy); from honestGateFromError. */
  gate?: HonestGate | null;
  /** Support reference (CO-…) shown on error/unavailable states — never a secret. */
  reference?: string | null;
  /** Per-surface override of the neutral heading. */
  title?: ReactNode;
  /** Per-surface override of the neutral body copy. */
  message?: ReactNode;
  /**
   * Domain glyph for the empty/gated/not-configured family (shown inside a navy
   * tint circle). Defaults to a magnifier; pass a domain-appropriate @vjal/ui
   * icon per surface (e.g. <IconUsers />). Purely presentational — it never
   * changes the posture, the data-honest-posture stamp, or the copy.
   */
  icon?: ReactNode;
  /** Wired to react-query refetch; only shown for retryable postures. */
  onRetry?: () => void;
  /** When true + posture==="empty", render the create CTA (affordances ONLY on empty). */
  canCreate?: boolean;
  createAction?: ReactNode;
  /** "page" (default) renders a PageLoading block; "rows" renders a table skeleton. */
  loadingVariant?: "page" | "rows";
  /** Skeleton sizing for loadingVariant="rows". */
  loadingRows?: number;
  loadingColumns?: number;
}

function asText(node: ReactNode, fallback: string): string {
  return typeof node === "string" ? node : fallback;
}

/**
 * Renders exactly one honest sub-state for the given posture. Callers pass the
 * posture straight through from useHonestQuery; `loading` is handled here so a
 * page can render `<HonestState posture={posture} …/>` without branching, then
 * render real content only when `posture === null` (ready-with-data).
 */
export function HonestState({
  posture,
  gate,
  reference,
  title,
  message,
  icon,
  onRetry,
  canCreate,
  createAction,
  loadingVariant = "page",
  loadingRows = 4,
  loadingColumns = 4,
}: HonestStateProps) {
  const copy = POSTURE_COPY[posture];
  const heading = title ?? copy.title;
  const body = message ?? copy.body;

  if (posture === "loading") {
    const label = asText(heading, "Loading");
    // Rows skeleton keeps its coaching-specific sizing + accessible label (knobs
    // @vjal/ui AsyncState does not expose); the full-page block delegates to
    // <AsyncState> (PageLoading, role=status) so the loading surface is shared.
    if (loadingVariant === "rows") {
      return (
        <div data-honest-posture="loading" aria-label={label}>
          <SkeletonRows rows={loadingRows} columns={loadingColumns} />
        </div>
      );
    }
    return (
      <div data-honest-posture="loading">
        <AsyncState status="loading" title={label} loadingVariant="page" />
      </div>
    );
  }

  // error / unavailable: delegate the Alert (alert role) + Retry rendering to
  // @vjal/ui <AsyncState>. The honest message — heading, neutral body, and the
  // verbatim support reference — is composed HERE (so the copy + the
  // "(reference: …)" format the triage e2e relies on are preserved) and handed
  // to AsyncState as the error message; Retry is wired only for retryable
  // postures. The message is never a stack.
  if (posture === "error" || posture === "unavailable") {
    const errorMessage = (
      <>
        <span className="font-semibold">{asText(heading, copy.title)}</span> — {asText(body, copy.body)}
        {reference ? ` (reference: ${reference})` : ""}
      </>
    );
    return (
      <div className="py-6" data-honest-posture={posture}>
        <AsyncState
          status="error"
          message={errorMessage}
          onRetry={onRetry && isRetryablePosture(posture) ? onRetry : undefined}
        />
      </div>
    );
  }

  // gate-closed: a warning Alert that explicitly states nothing was changed, so
  // a closed lane can never imply a write happened (no-fabrication gate). Kept
  // local because AsyncState only renders the error (not warning) variant.
  if (posture === "gate-closed") {
    const gateReason = gate?.reason ? gate.reason : asText(body, copy.body);
    return (
      <div className="py-6" data-honest-posture="gate-closed">
        <Alert variant="warning">
          <span className="font-semibold">{asText(heading, copy.title)}</span> — {gateReason} No change was made.
        </Alert>
      </div>
    );
  }

  // empty / gated / not-configured / permission-denied / maturity-gated all use
  // the neutral EmptyState — tone differences are carried by copy, not error
  // color. Affordances (the create CTA) render ONLY on `empty`. The domain glyph
  // sits in a navy tint circle; it is decoration only and never alters posture.
  // Kept local: AsyncState's empty surface carries neither the navy-circle glyph
  // nor the create-only-on-empty affordance rule this family requires.
  const glyph = icon ?? <IconSearch size={22} />;
  return (
    <div data-honest-posture={posture}>
      <EmptyState
        icon={
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-vjal-50 text-vjal-500">
            {glyph}
          </span>
        }
        title={asText(heading, copy.title)}
        action={posture === "empty" && canCreate ? createAction : undefined}
      >
        {asText(body, copy.body)}
      </EmptyState>
    </div>
  );
}
